// Canonical API-error → user-facing copy resolver. The wire envelope and
// `DomainError` both carry a stable machine code (e.g. `UNAUTHENTICATED`,
// `identity/photo_invalid`); `DomainError.message` is set to that code, so
// rendering `err.message` leaks the raw code to users. Route every
// error-display site through here instead: it maps the code to the curated
// copy in `messages/en/errors.json` and falls back when the code is unknown.
//
// `errorMessage` is pure (no React hook) so it works in mutation files and
// non-render callbacks, but it can only speak English. Components render
// through `useErrorMessage`, which resolves the same code through the active
// locale's `Errors` catalog.

import { useCallback } from 'react';
import { useTranslations } from 'use-intl';
import errorCopy from '../../messages/en/errors.json';
import { parseApiErrorCode } from './api-error';

type ErrorCode = keyof typeof errorCopy;

// English is the key-set source of truth: every non-default catalog is
// deep-merged over it (see `messages/load.ts`), so a key present here
// resolves in every locale.
function isErrorCode(code: string): code is ErrorCode {
  return Object.hasOwn(errorCopy, code);
}

const DEFAULT_FALLBACK: string = errorCopy.UNKNOWN;

function codeOf(err: unknown): string | null {
  const nested = parseApiErrorCode(err);
  if (nested !== null) return nested;
  if (err !== null && typeof err === 'object') {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * Resolve a thrown API error (or `{ code }`/`{ error: { code } }` shape) to
 * curated user-facing copy. Unknown codes and code-less errors return
 * `fallback` (default: the generic `UNKNOWN` message).
 */
export function errorMessage(err: unknown, fallback: string = DEFAULT_FALLBACK): string {
  const code = codeOf(err);
  if (code !== null && isErrorCode(code)) return errorCopy[code];
  return fallback;
}

/** Localized counterpart of `errorMessage` for render-time copy. */
export function useErrorMessage(): (err: unknown, fallback: string) => string {
  const t = useTranslations('Errors');
  return useCallback(
    (err: unknown, fallback: string): string => {
      const code = codeOf(err);
      return code !== null && isErrorCode(code) ? t(code) : fallback;
    },
    [t],
  );
}
